// Utility functions for building and managing the Chart of Accounts tree

import { AccountTypeResponse, AccountGroupResponse, AccountResponse } from '@/app/types';
import { TreeNode, FlattenedTreeData } from './treeTypes';

/**
 * Build a flat list of tree nodes from account types, groups, and accounts
 */
export function buildTreeNodes(
  accountTypes: AccountTypeResponse[],
  accountGroups: AccountGroupResponse[],
  accounts: AccountResponse[]
): TreeNode[] {
  const nodes: TreeNode[] = [];

  // Index groups by account_type_id and parent_id for efficient lookup
  const groupsByTypeId = new Map<number, AccountGroupResponse[]>();
  const groupsByParentId = new Map<number | null, AccountGroupResponse[]>();

  accountGroups.forEach(group => {
    // By type
    const byType = groupsByTypeId.get(group.account_type_id) || [];
    byType.push(group);
    groupsByTypeId.set(group.account_type_id, byType);

    // By parent
    const byParent = groupsByParentId.get(group.parent_id) || [];
    byParent.push(group);
    groupsByParentId.set(group.parent_id, byParent);
  });

  // Index accounts by group_id
  const accountsByGroupId = new Map<number, AccountResponse[]>();
  accounts.forEach(account => {
    const byGroup = accountsByGroupId.get(account.account_group_id) || [];
    byGroup.push(account);
    accountsByGroupId.set(account.account_group_id, byGroup);
  });

  // Build tree recursively
  function addGroupNode(group: AccountGroupResponse, level: number, parentNodeId?: string) {
    const groupNodeId = `group-${group.id}`;
    const childGroups = accountGroups.filter(g => g.parent_id === group.id);
    const groupAccounts = accountsByGroupId.get(group.id) || [];

    nodes.push({
      id: groupNodeId,
      nodeType: 'account_group',
      name: group.name,
      code: group.code,
      level,
      isActive: group.is_active,
      hasChildren: childGroups.length > 0 || groupAccounts.length > 0,
      rawId: group.id,
      parentNodeId,
      accountTypeId: group.account_type_id,
    });

    // Add child groups recursively
    childGroups
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(childGroup => {
        addGroupNode(childGroup, level + 1, groupNodeId);
      });

    // Add accounts under this group
    groupAccounts
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(account => {
        nodes.push({
          id: `account-${account.id}`,
          nodeType: 'account',
          name: account.name,
          code: account.code,
          level: level + 1,
          isActive: account.is_active,
          hasChildren: false,
          rawId: account.id,
          parentNodeId: groupNodeId,
          accountGroupId: account.account_group_id,
        });
      });
  }

  // Sort account types by name
  const sortedTypes = [...accountTypes].sort((a, b) => a.name.localeCompare(b.name));

  sortedTypes.forEach(type => {
    const typeNodeId = `type-${type.id}`;

    // Get root groups for this type (no parent)
    const rootGroups = accountGroups.filter(
      g => g.account_type_id === type.id && !g.parent_id
    );

    nodes.push({
      id: typeNodeId,
      nodeType: 'account_type',
      name: type.name,
      level: 0,
      nature: type.nature,
      hasChildren: rootGroups.length > 0,
      rawId: type.id,
    });

    // Add root groups and their children
    rootGroups
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(group => {
        addGroupNode(group, 1, typeNodeId);
      });
  });

  return nodes;
}

/**
 * Get visible nodes based on expanded state
 */
export function getVisibleNodes(
  allNodes: TreeNode[],
  expandedIds: Set<string>
): TreeNode[] {
  const visible: TreeNode[] = [];
  const hiddenParentIds = new Set<string>();

  for (const node of allNodes) {
    // Check if any ancestor is collapsed
    let isHidden = false;
    let currentParentId = node.parentNodeId;

    while (currentParentId) {
      if (!expandedIds.has(currentParentId) || hiddenParentIds.has(currentParentId)) {
        isHidden = true;
        break;
      }
      // Find parent node to get its parent
      const parentNode = allNodes.find(n => n.id === currentParentId);
      currentParentId = parentNode?.parentNodeId;
    }

    if (!isHidden) {
      visible.push(node);
    } else {
      hiddenParentIds.add(node.id);
    }
  }

  return visible;
}

/**
 * Build flattened tree data structure
 */
export function buildFlattenedTreeData(
  accountTypes: AccountTypeResponse[],
  accountGroups: AccountGroupResponse[],
  accounts: AccountResponse[],
  expandedIds: Set<string>
): FlattenedTreeData {
  const allNodes = buildTreeNodes(accountTypes, accountGroups, accounts);
  const visibleNodes = getVisibleNodes(allNodes, expandedIds);
  const nodeMap = new Map(allNodes.map(node => [node.id, node]));

  return {
    visibleNodes,
    allNodes,
    nodeMap,
  };
}

/**
 * Get all descendant node IDs for a given node
 */
export function getDescendantIds(nodeId: string, allNodes: TreeNode[]): string[] {
  const descendants: string[] = [];
  const queue = [nodeId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const children = allNodes.filter(n => n.parentNodeId === currentId);

    for (const child of children) {
      descendants.push(child.id);
      queue.push(child.id);
    }
  }

  return descendants;
}

/**
 * Get all node IDs that should be expanded to show a particular node
 */
export function getAncestorIds(nodeId: string, allNodes: TreeNode[]): string[] {
  const ancestors: string[] = [];
  const nodeMap = new Map(allNodes.map(n => [n.id, n]));

  let currentNode = nodeMap.get(nodeId);
  while (currentNode?.parentNodeId) {
    ancestors.push(currentNode.parentNodeId);
    currentNode = nodeMap.get(currentNode.parentNodeId);
  }

  return ancestors;
}

/**
 * Filter nodes by search query
 */
export function filterNodesBySearch(
  allNodes: TreeNode[],
  searchQuery: string,
  showInactive: boolean
): TreeNode[] {
  const query = searchQuery.toLowerCase().trim();

  if (!query && showInactive) {
    return allNodes;
  }

  const matchingIds = new Set<string>();
  const ancestorIds = new Set<string>();

  // First pass: find matching nodes
  for (const node of allNodes) {
    // Filter inactive if needed
    if (!showInactive && node.isActive === false) {
      continue;
    }

    // Check search match
    if (query) {
      const nameMatch = node.name.toLowerCase().includes(query);
      const codeMatch = node.code?.toLowerCase().includes(query);

      if (nameMatch || codeMatch) {
        matchingIds.add(node.id);
        // Add all ancestors to ensure they're visible
        getAncestorIds(node.id, allNodes).forEach(id => ancestorIds.add(id));
      }
    } else {
      matchingIds.add(node.id);
    }
  }

  // Include matching nodes and their ancestors
  return allNodes.filter(node => {
    if (!showInactive && node.isActive === false) {
      return false;
    }
    return matchingIds.has(node.id) || ancestorIds.has(node.id);
  });
}
